import {
  Box,
  Breadcrumbs,
  Button,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import Loading from 'components/common/Loading';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import FolderOutlineIcon from 'mdi-react/FolderOutlineIcon';
import { arrayOf, func, object, string } from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useListSharedFilesQuery } from 'store/app/entities/SharedFilesSlice';
import { I18n } from 'utils/i18n';

const SharedFolderPicker = ({ value, onChange, selectableFolders = [] }) => {
  const [browseId, setBrowseId] = useState(null);

  const selectableById = useMemo(
    () => new Map((selectableFolders || []).map(f => [String(f._id), f])),
    [selectableFolders],
  );

  const { data: { data } = {}, isLoading } = useListSharedFilesQuery({
    folder: 'shared',
    id: browseId || undefined,
    page: 1,
    perPage: 200,
    sortBy: 'name',
    sort: 'asc',
  });

  const parent = data?.parent;
  const childFolders = useMemo(
    () => (data?.sharedFiles || []).filter(f => f.kind === 'Folder'),
    [data?.sharedFiles],
  );

  useEffect(() => {
    if (value) setBrowseId(String(value));
  }, [value]);

  const currentFolderId = parent?._id ? String(parent._id) : null;
  const canSelectCurrent = currentFolderId && selectableById.has(currentFolderId);
  const selectedLabel = value ? selectableById.get(String(value))?.label : null;

  const breadcrumbItems = useMemo(() => {
    const items = [
      {
        key: 'root',
        label: I18n.t('DocumentsScene.Folders.shared'),
        onClick: () => setBrowseId(null),
      },
    ];
    (parent?.ancestors || []).forEach(ancestor => {
      items.push({
        key: ancestor._id,
        label: ancestor.name,
        onClick: () => setBrowseId(String(ancestor._id)),
      });
    });
    if (parent?.name) {
      items.push({ key: parent._id, label: parent.name, onClick: null });
    }
    return items;
  }, [parent]);

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 280,
        maxHeight: 360,
      }}
    >
      <Breadcrumbs
        aria-label="folder"
        sx={{
          p: '10px 12px',
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'background.default',
        }}
      >
        {breadcrumbItems.map((item, idx) => {
          const isLast = idx === breadcrumbItems.length - 1;
          if (isLast || !item.onClick) {
            return (
              <Typography
                key={item.key}
                variant="body2"
                sx={{
                  color: 'text.primary',
                }}
              >
                {item.label}
              </Typography>
            );
          }
          return (
            <Link
              key={item.key}
              component="button"
              variant="body2"
              underline="hover"
              color="inherit"
              onClick={item.onClick}
              sx={{ cursor: 'pointer', border: 0, background: 'none', p: 0 }}
            >
              {item.label}
            </Link>
          );
        })}
      </Breadcrumbs>

      {isLoading ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Loading size={32} />
        </Box>
      ) : (
        <List dense disablePadding sx={{ flex: 1, overflowY: 'auto', p: 0 }}>
          {childFolders.length === 0 ? (
            <Box
              sx={{
                p: 3,
                textAlign: 'center',
                color: 'text.secondary',
                fontSize: theme => theme.typography.pxToRem(13),
              }}
            >
              {I18n.t('AgenticChatFlow.KnowledgeFolderPickerEmpty')}
            </Box>
          ) : (
            childFolders.map(folder => (
              <ListItemButton key={folder._id} onClick={() => setBrowseId(String(folder._id))}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <FolderOutlineIcon size={20} />
                </ListItemIcon>
                <ListItemText primary={folder.name} />
                <ChevronRightIcon size={20} />
              </ListItemButton>
            ))
          )}
        </List>
      )}

      <Box
        sx={{
          p: '10px 12px',
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          backgroundColor: 'background.default',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            minWidth: 0,
          }}
        >
          {selectedLabel && String(value) === currentFolderId
            ? selectedLabel
            : currentFolderId
              ? I18n.t('AgenticChatFlow.KnowledgeFolderPickerHint')
              : I18n.t('AgenticChatFlow.KnowledgeFolderPickerOpenFolder')}
        </Typography>
        <Button
          variant="contained"
          size="small"
          disableElevation
          disabled={!canSelectCurrent}
          onClick={() => onChange?.(currentFolderId)}
        >
          {I18n.t('AgenticChatFlow.KnowledgeFolderPickerSelect')}
        </Button>
      </Box>
    </Box>
  );
};

SharedFolderPicker.propTypes = {
  value: string,
  onChange: func,
  selectableFolders: arrayOf(object),
};

export default SharedFolderPicker;
